import React, { useState, useEffect, useRef } from "react";
import { generateQuoteDocumentHTML } from "./generateQuoteDocument";
import QuoteRespondBar from "./QuoteRespondBar";


export default function QuotePage({ quoteId, crmUser }) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [html, setHtml] = useState(null);
  const [respond, setRespond] = useState(null);
  const [totalExVat, setTotalExVat] = useState("");
  const iframeRef = useRef(null);
  // The private key from the client's email link (?k=), if any.
  const linkKey = (() => { try { return new URLSearchParams(window.location.search).get("k") || ""; } catch (e) { return ""; } })();

  useEffect(() => {
    async function load() {
      try {
        // Just this quote, from the server (api/quote/[ref].js), with
        // internal figures removed - never the whole Quote Log.
        const res = await fetch(`/api/quote?ref=${encodeURIComponent(quoteId)}${linkKey ? `&k=${encodeURIComponent(linkKey)}` : ""}`);
        const data = await res.json().catch(() => ({}));
        if (!res.ok || !data.quote) { setError(data.error || "Quote not found."); setLoading(false); return; }
        const quote = data.quote;
        setRespond(data.respond || null);
        setTotalExVat(quote["Total (ex VAT)"] || "");

        const generatedHtml = await generateQuoteDocumentHTML(quote);

        setHtml(generatedHtml);
        setLoading(false);
      } catch (e) {
        setError(e.message);
        setLoading(false);
      }
    }
    load();
  }, [quoteId]); // eslint-disable-line react-hooks/exhaustive-deps

  const handlePrint = () => {
    const iframe = iframeRef.current;
    if (iframe && iframe.contentWindow) {
      iframe.contentWindow.focus();
      iframe.contentWindow.print();
    }
  };

  // Signed-in back bar: the Quote Log with this quote's panel open.
  const quoteLogHref = `/?open=${encodeURIComponent(quoteId)}`;
  const toLog = () => { try { window.sessionStorage.setItem("sublime_active_tab", "log"); } catch (err) { /* storage blocked */ } };
  // While loading, or if the quote can't be shown, a signed-in user still
  // gets a way to the Quote Log (clients see just the message, as before).
  const logLink = crmUser && <a href={quoteLogHref} onClick={toLog} style={{ fontSize: 14, color: "#3a52cc" }}>← Back to Quote Log</a>;

  if (loading) return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16, alignItems: "center", justifyContent: "center", height: "100vh", fontFamily: "IBM Plex Sans,sans-serif", fontSize: 16, color: "#1D3461" }}>
      Loading quote...
      {logLink}
    </div>
  );

  if (error) return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16, alignItems: "center", justifyContent: "center", height: "100vh", fontFamily: "IBM Plex Sans,sans-serif", fontSize: 16, color: "#E53935" }}>
      {error}
      {logLink}
    </div>
  );

  // A full-height column: navbar, the Accept bar, then the quote filling the
  // rest, so the bar never pushes the quote off the bottom of the screen.
  return (
    <div className="quote-shell" style={{ display: "flex", flexDirection: "column" }}>
      <style>{`
        .quote-shell { height: 100vh; height: 100dvh; }
        @media screen {
          .quote-navbar {
            background: #0e1520;
            border-bottom: 1px solid #1e2d42;
            flex: none;
            z-index: 100;
            padding: 12px 32px;
            display: flex;
            align-items: center;
            justify-content: space-between;
            gap: 10px;
            flex-wrap: wrap;
            font-family: 'IBM Plex Sans', sans-serif;
          }
        }
        @media screen and (max-width: 600px) {
          .quote-navbar { padding: 10px 14px; }
          .quote-navbar-brand { display: none; }
        }
      `}</style>

      <div className="quote-navbar">
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          {crmUser && (
            <>
              {/* Signed-in only. Always goes straight to the Quote Log (this
                  quote's panel open), in this tab - never history back or
                  closing the tab: opened from a bookmark, there's nothing in
                  the app to go back to, and iPhone Safari closed the tab. */}
              <a href={quoteLogHref} onClick={toLog} aria-label="Sublime Precision Cleaning: Quote Log" style={{ display: "inline-flex", alignItems: "center" }}>
                <img src="/logo.png" alt="Sublime Precision Cleaning" style={{ height: 26, width: "auto", display: "block" }} />
              </a>
              <a
                href={quoteLogHref}
                onClick={toLog}
                style={{ color: "#fff", fontSize: 13, fontWeight: 700, textDecoration: "none", padding: "8px 12px", borderRadius: 6, border: "1px solid #2a3b55" }}
              >
                ← Back to Quote Log
              </a>
              <span style={{ color: "#1e2d42" }}>|</span>
            </>
          )}
          <span className="quote-navbar-brand" style={{ color: "#7a8fa8", fontSize: 12, letterSpacing: "0.05em", textTransform: "uppercase", fontWeight: 600 }}>Sublime Precision Cleaning</span>
          <span style={{ color: "#1e2d42" }}>|</span>
          <span style={{ color: "#7a8fa8", fontSize: 12 }}>Quote View</span>
        </div>
        <button
          onClick={handlePrint}
          style={{ background: "#3a52cc", color: "#fff", border: "none", padding: "10px 24px", borderRadius: 6, fontWeight: 700, fontSize: 12, cursor: "pointer", letterSpacing: "0.05em" }}
        >
          Save as PDF
        </button>
      </div>

      <QuoteRespondBar quoteId={quoteId} linkKey={linkKey} respond={respond} onRespond={setRespond} totalExVat={totalExVat} crmUser={crmUser} />

      <iframe
        ref={iframeRef}
        srcDoc={html}
        style={{ width: "100%", flex: 1, minHeight: 0, border: "none", display: "block" }}
        title="Quote"
      />
    </div>
  );
}
